import { useState, useEffect } from "react";
import { Box, IconButton, CircularProgress, Typography } from "@mui/material";
import { MoreVert as MoreVertIcon, Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import ActionsMenu from "../../components/ActionsMenu";
import DeleteConfirmationDialog from "../../components/DeleteDialog";
import DataTable from "../../components/DataTable";
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useTranslation } from 'react-i18next';
const Sales = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedSaleId, setSelectedSaleId] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const apiUrl = "https://oven-backend.onrender.com";


  useEffect(() => {
    const fetchSales = async () => {
      try {
        const response = await axios.get(`${apiUrl}/sales`);
        setSales(response.data);
      } catch (error) {
        console.error("Error fetching sales:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSales();
  }, []);

  const handleMenuOpen = (event, saleId) => {
    setAnchorEl(event.currentTarget);
    setSelectedSaleId(saleId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditSale = () => {
    navigate(`/UpdateInvoices/${selectedSaleId}`);
    handleMenuClose();
  };

  const handleViewDetails = () => {
    navigate(`/ViewInvoice/${selectedSaleId}`);
    handleMenuClose();
  };

  const handleDeleteDialogOpen = () => {
    setOpenDeleteDialog(true);
    handleMenuClose();
  };

  const handleDeleteDialogClose = () => {
    setOpenDeleteDialog(false);
  };

  const handleConfirmDelete = async () => {
    try {
      
     const res = await axios.delete(`${apiUrl}/sales/${selectedSaleId}`);
      setSales((prevSales) => prevSales.filter(sale => sale._id !== selectedSaleId));
      console.log("Invoice Data:",res.data);

    } catch (error) {
      console.error("Error deleting sale:", error);
    } finally {
      handleDeleteDialogClose();
    }
  };

  const columns = [
    { 
      field: "employee_id", 
      headerName: t('customer_name'), 
      flex: 1,
      valueGetter: (params) => params.row.employee_id?.name || t('n/a'),
      headerAlign: 'center',
      align: isRTL ? 'right' : 'left'
    },
    { 
      field: "total_amount", 
      headerName: t('total_cost'), 
      flex: 1,
      renderCell: (params) => (
        <Typography color="green" sx={{ direction: 'ltr' }}>
          ${params.row.total_amount}
        </Typography>
      ),
      headerAlign: 'center',
      align: 'center'
    },
    { 
      field: "date", 
      headerName: t('date'), 
      flex: 1,
      valueGetter: ({ value }) => value ? 
        new Date(value).toLocaleDateString(i18n.language, { 
          year: 'numeric', 
          month: '2-digit', 
          day: '2-digit' 
        }) : t('n/a'),
      headerAlign: 'center',
      align: 'center'
    },
    { 
      field: "payment_status", 
      headerName: t('payment_status'), 
      flex: 1,
      headerAlign: 'center',
      align: 'center'
    },
    { 
      field: "inventory_status", 
      headerName: t('inventory_status'), 
      flex: 1,
      headerAlign: 'center',
      align: 'center'
    },
    {
      field: "actions",
      headerName: t('actions'),
      flex: 0.5,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', justifyContent: isRTL ? 'flex-start' : 'flex-end' }}>
          <IconButton onClick={(event) => handleMenuOpen(event, params.row._id)}>
            <MoreVertIcon />
          </IconButton>
          <ActionsMenu 
            anchorEl={anchorEl}
            handleMenuClose={handleMenuClose}
            menuItems={[
              { 
                label: t('edit_sale'), 
                action: handleEditSale, 
                icon: <EditIcon sx={{ transform: isRTL ? 'scaleX(-1)' : 'none' }}/> 
              },
              { 
                label: t('view_details'), 
                action: handleViewDetails, 
                icon: <VisibilityIcon sx={{ transform: isRTL ? 'scaleX(-1)' : 'none' }}/> 
              },
              { 
                label: t('delete_sale'), 
                action: handleDeleteDialogOpen, 
                icon: <DeleteIcon sx={{ transform: isRTL ? 'scaleX(-1)' : 'none' }}/>, 
                style: { color: "red" } 
              }
            ]}
            direction={isRTL ? 'right' : 'left'}
          />
        </Box>
      ),
      headerAlign: 'center',
      align: 'center'
    },
  ];

  return (
    <Box m="20px" sx={{ direction: isRTL ? 'rtl' : 'ltr' }}>
      {loading ? (
        <CircularProgress />
      ) : (
        <DataTable 
          title={t('sales_list')}
          columns={columns}
          rows={sales} 
          getRowId={(row) => row._id}
          sx={{
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: theme => theme.palette.mode === 'dark' ? '#1e1e1e' : '#f5f5f5',
              borderColor: theme => theme.palette.divider,
            },
            '& .MuiDataGrid-cell': {
              borderColor: theme => theme.palette.divider,
            },
          }}
        />
      )}

      <DeleteConfirmationDialog
        open={openDeleteDialog} 
        onClose={handleDeleteDialogClose} 
        onConfirm={handleConfirmDelete} 
        title={t('delete_sale')}
        message={t('delete_sale_confirmation')}
        confirmColor="error"
        cancelColor="secondary"
        size="large"
        isRTL={isRTL}
      />
    </Box>
  );
};

export default Sales;